import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  ArrowLeft, UserCheck, Search as SearchIcon,
  ChevronRight, ChevronDown, IdCard, UserPlus, Crown, PhoneIncoming, Hand,
  CircleDashed, ArrowRightLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePageHeader } from '../context/PageHeaderContext';

/**
 * /admin/assignments — list every external number that has touched the
 * system (business-card submission, manual entry, inbound call, outbound
 * call, etc.) with its current assignment + how/when/why it was assigned.
 *
 * Backed by GET /api/twilio/admin/assignments.
 */

const METHOD_META = {
  business_card_submission: {
    label: 'Business Card Submission',
    icon: IdCard,
    color: '#8b5cf6',
  },
  manual_entry: {
    label: 'Manual Entry',
    icon: UserPlus,
    color: '#586D52',
  },
  supervisor_steal: {
    label: 'Supervisor Steal',
    icon: Crown,
    color: '#f59e0b',
  },
  unassigned_caller_assignment: {
    label: 'Unassigned Caller Assignment',
    icon: PhoneIncoming,
    color: '#3b82f6',
  },
  pre_assignment_acceptance: {
    label: 'Pre-Assignment Acceptance',
    icon: Hand,
    color: '#10b981',
  },
};

const DURATION_LABEL = {
  '1d': '1 day',
  '3d': '3 days',
  '7d': '7 days',
  '30d': '30 days',
  permanent: 'Permanent',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const Row = ({ item, expanded, onToggle, onReassign }) => {
  const meta = item.method ? METHOD_META[item.method] : null;
  const Icon = meta?.icon || CircleDashed;
  const assigned = item.assigned_user;
  return (
    <div
      className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-[#1a1a1a]/70"
      data-testid={`assignment-row-${item.id}`}
    >
      <div className="flex items-stretch">
        <button onClick={onToggle} className="flex-1 flex items-center gap-3 p-3 text-left">
          {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={meta ? { backgroundColor: meta.color + '22', color: meta.color } : { backgroundColor: '#9ca3af22', color: '#6b7280' }}
          >
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">
              {item.name || item.phone || 'Unknown'}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              <span className="font-mono">{item.phone || '—'}</span>
              {assigned ? (
                <> · <span>→ {assigned.name}</span></>
              ) : (
                <> · <span className="text-amber-600">Unassigned</span></>
              )}
            </p>
          </div>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onReassign(item); }}
          className="px-3 flex items-center gap-1.5 text-xs text-[#586D52] hover:bg-[#586D52]/10 rounded-r-xl transition-colors border-l border-gray-200 dark:border-gray-700"
          data-testid={`assignment-reassign-${item.id}`}
          title="Reassign this contact"
        >
          <ArrowRightLeft className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Reassign</span>
        </button>
      </div>
      {expanded && (
        <div className="px-4 pb-3 pt-1 border-t border-gray-100 dark:border-gray-800 space-y-2 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Date assigned</p>
              <p>{fmtDate(item.last_assigned_at)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Method</p>
              <p>
                {meta?.label || (assigned ? 'Manual Entry' : 'Not yet assigned')}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Assigned to</p>
              <p>
                {assigned ? `${assigned.name}${assigned.role ? ` (${assigned.role})` : ''}` : 'Unassigned'}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">State</p>
              <p>{assigned?.state || '—'}</p>
            </div>
          </div>

          {item.method === 'supervisor_steal' && item.method_detail && (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 p-2.5">
              <p className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-1">Steal details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-3">
                <div>
                  <span className="text-muted-foreground">Duration:</span>{' '}
                  <span className="font-medium">{DURATION_LABEL[item.method_detail.duration] || 'Permanent'}</span>
                </div>
                {item.method_detail.reverts_at && (
                  <div>
                    <span className="text-muted-foreground">Reverts on:</span>{' '}
                    <span className="font-medium">{fmtDate(item.method_detail.reverts_at)}</span>
                  </div>
                )}
                {item.method_detail.original_owner && (
                  <div>
                    <span className="text-muted-foreground">Stolen from:</span>{' '}
                    <span className="font-medium">{item.method_detail.original_owner.name}</span>
                  </div>
                )}
                {item.method_detail.reverts_to && (
                  <div>
                    <span className="text-muted-foreground">Reverts to:</span>{' '}
                    <span className="font-medium">{item.method_detail.reverts_to.name}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {item.tags && item.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {item.tags.map((t) => (
                <span key={t} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 dark:bg-[#0d0d0d] text-muted-foreground">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AssignmentsPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Special users with the `admin_assignments` toggle get the same
  // reassign + move-to-pool privileges as full admins.
  const canAccess = isAdmin || (user?.feature_toggles || {}).admin_assignments === true;
  usePageHeader({ title: 'Assignments', back: '/admin/calling' }, []);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [q, setQ] = useState('');

  // Reassign-to-any-user state.
  const [reassigning, setReassigning] = useState(null); // the row being reassigned
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/twilio/admin/assignments');
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error('Could not load assignments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canAccess) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/twilio/admin/assignments');
        if (!cancelled) setItems(res.data?.items || []);
      } catch (e) {
        if (!cancelled) toast.error('Could not load assignments');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, canAccess]);

  // Lazy-load the user list on first reassign click.
  useEffect(() => {
    if (!reassigning || allUsers.length > 0) return;
    api.get('/users').then((r) => setAllUsers(r.data || [])).catch(() => {});
  }, [reassigning, allUsers.length, api]);

  const reassignTo = async (targetUserId) => {
    if (!reassigning || !targetUserId) return;
    setSubmitting(true);
    try {
      await api.post(`/twilio/contacts/${reassigning.id}/assign`, {
        assignment_type: 'individual',
        assigned_to_user_id: targetUserId,
      });
      toast.success('Contact reassigned');
      setReassigning(null);
      setUserSearch('');
      // Refresh the assignments list so the row shows the new owner.
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reassign failed');
    } finally {
      setSubmitting(false);
    }
  };

  const unassignContact = async () => {
    if (!reassigning) return;
    setSubmitting(true);
    try {
      await api.post(`/twilio/contacts/${reassigning.id}/assign`, {
        assignment_type: 'unassigned',
        assigned_to_user_id: null,
      });
      toast.success('Contact moved back to Unassigned pool');
      setReassigning(null);
      setUserSearch('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unassign failed');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const needle = userSearch.trim().toLowerCase();
    if (!needle) return allUsers;
    return allUsers.filter((u) => {
      const full = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
      return full.includes(needle) || (u.email || '').toLowerCase().includes(needle);
    });
  }, [allUsers, userSearch]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    const has = (s) => s && String(s).toLowerCase().includes(needle);
    return items.filter((it) =>
      has(it.name) || has(it.phone) || has(it.assigned_user?.name)
    );
  }, [items, q]);

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-background p-8 flex flex-col items-center justify-center text-center">
        <p className="text-lg font-medium">Admin only</p>
        <Button className="mt-4" onClick={() => navigate('/')}>Back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] pb-24" data-testid="assignments-page">
      <div className="max-w-3xl mx-auto p-3">
        <div className="relative mb-3">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by contact name, user name, or phone…"
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/70 dark:bg-[#1a1a1a]/70 border border-gray-200 dark:border-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-[#586D52]"
            data-testid="assignments-search"
          />
        </div>

        <Card className="rounded-2xl">
          <CardContent className="pt-4 space-y-2">
            {loading && <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>}
            {!loading && filtered.length === 0 && (
              <p className="text-sm text-muted-foreground py-6 text-center">
                {q ? 'No matches.' : 'No external numbers in the system yet.'}
              </p>
            )}
            {!loading && filtered.map((item) => (
              <Row
                key={item.id}
                item={item}
                expanded={expanded === item.id}
                onToggle={() => setExpanded((cur) => (cur === item.id ? null : item.id))}
                onReassign={(it) => setReassigning(it)}
              />
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Reassign dialog */}
      <Dialog open={!!reassigning} onOpenChange={(open) => !open && setReassigning(null)}>
        <DialogContent data-testid="reassign-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reassign contact</DialogTitle>
            <DialogDescription>
              {reassigning?.name || reassigning?.phone || 'Contact'} —{' '}
              currently {reassigning?.assigned_user
                ? <>assigned to <strong>{reassigning.assigned_user.name}</strong></>
                : <span className="text-amber-600">unassigned</span>}.
              Pick a new owner.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search users by name or email…"
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-white dark:bg-[#0d0d0d] border border-gray-200 dark:border-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-[#586D52]"
                data-testid="reassign-user-search"
                autoFocus
              />
            </div>
            <div className="max-h-72 overflow-auto space-y-1">
              {filteredUsers.length === 0 && (
                <p className="text-xs text-center text-muted-foreground py-4">No matches.</p>
              )}
              {filteredUsers.map((u) => {
                const full = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
                const isCurrent = reassigning?.assigned_user?.id === u.id;
                return (
                  <button
                    key={u.id}
                    onClick={() => reassignTo(u.id)}
                    disabled={submitting || isCurrent}
                    className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-3 hover:bg-[#586D52]/10 transition-colors ${isCurrent ? 'opacity-50 cursor-default' : ''}`}
                    data-testid={`reassign-user-${u.id}`}
                  >
                    <div className="w-7 h-7 rounded-full bg-[#586D52]/15 text-[#586D52] flex items-center justify-center text-xs font-semibold flex-shrink-0">
                      {(u.first_name || u.email || '?')[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{full}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {u.role || 'user'}{u.state ? ` · ${u.state}` : ''}
                      </p>
                    </div>
                    {isCurrent && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">current</span>}
                  </button>
                );
              })}
            </div>
            {reassigning?.assigned_user && (
              <Button
                variant="outline"
                className="w-full text-amber-700 border-amber-200"
                onClick={unassignContact}
                disabled={submitting}
                data-testid="reassign-unassign-btn"
              >
                Move back to Unassigned pool
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssignmentsPage;
